import { useCallback } from "react";

import { type MylensesSubmissionDetailsSource } from "@snapchat/blizzard-schema";
import { useRecoilState } from "recoil";

import { type Discoverability, type Visibility } from "../../../../gql-generated/types";
import { type LensPreviewStateHook, useLensPreviewState } from "../../../../lens-preview/hooks";
import { GAME_LENS_VIDEO_DURATION_REQUIREMENT } from "../../../../lens-preview/validation";
import { useTags } from "../../../../lens-tags/hooks";
import { useVisibility } from "../../../../lens-visibility/hooks";
import {
    discoverabilityById,
    isGameUserProvidedById,
    lensCreatorPayoutEnrolledById,
    lensDescriptionAtomById,
    lensPreviewMediaItemsAtomById,
    lensSpectaclesTagsAtomById,
    lensTagsAtomById,
    lensVisibilityAtomById,
    releaseNoteAtomById,
} from "../../../../shared/state";

export interface EditDetailsResult {
    tagsList: readonly string[];
    addTagItem: (value: string) => void;
    removeTag: (value: string) => void;
    spectaclesTagsList: readonly string[];
    removeSpectaclesTag: (tag: string) => void;
    addSpectaclesTag: (value: string) => void;
    visibility: Visibility;
    changeVisibility: (value: Visibility) => void;
    lensPreviewProps: LensPreviewStateHook;
    discoverability: Discoverability | null;
    setDiscoverability: (discoverability: Discoverability | null) => void;
    isGameUserProvided: boolean;
    setIsGameUserProvided: (isGameUserProvided: boolean) => void;
    lensCreatorPayoutEnrolled: boolean;
    setLensCreatorPayoutEnrolled: (lensCreatorPayoutEnrolled: boolean) => void;
    lensDescription: string;
    setLensDescription: (lensDescription: string) => void;
    releaseNote: string | null;
    setReleaseNote: (releaseNote: string) => void;
}

export const useEditDetails = (
    lensOrSubmissionId: string,
    onChange: (source: MylensesSubmissionDetailsSource) => () => void,
): EditDetailsResult => {
    const { tagsList, addTag, removeTag } = useTags(lensTagsAtomById(lensOrSubmissionId));
    const addTagItem = useCallback(
        (value: string) => {
            addTag(value);
            onChange("TAGS")();
        },
        [addTag, onChange],
    );

    const {
        tagsList: spectaclesTagsList,
        addTag: addSpectaclesTag,
        removeTag: removeSpectaclesTag,
    } = useTags(lensSpectaclesTagsAtomById(lensOrSubmissionId));

    const { visibility, setVisibility } = useVisibility(lensVisibilityAtomById(lensOrSubmissionId));

    const changeVisibility = useCallback(
        (value: Visibility) => {
            setVisibility(value);
            onChange("VISIBILITY")();
        },
        [setVisibility, onChange],
    );

    const [previewMediaItems, setPreviewMediaItems] = useRecoilState(lensPreviewMediaItemsAtomById(lensOrSubmissionId));
    const [isGameUserProvided, setIsGameUserProvided] = useRecoilState(isGameUserProvidedById(lensOrSubmissionId));

    const lensPreviewProps = useLensPreviewState({
        previewMediaItems,
        setPreviewMediaItems,
        videoDurationRequirement: isGameUserProvided ? GAME_LENS_VIDEO_DURATION_REQUIREMENT : undefined,
    });

    const [discoverability, setDiscoverability] = useRecoilState(discoverabilityById(lensOrSubmissionId));
    const [lensCreatorPayoutEnrolled, setLensCreatorPayoutEnrolled] = useRecoilState(
        lensCreatorPayoutEnrolledById(lensOrSubmissionId),
    );
    const [lensDescription, setLensDescription] = useRecoilState(lensDescriptionAtomById(lensOrSubmissionId));
    const [releaseNote, setReleaseNote] = useRecoilState(releaseNoteAtomById(lensOrSubmissionId));

    return {
        tagsList,
        addTagItem,
        removeTag,
        spectaclesTagsList,
        removeSpectaclesTag,
        addSpectaclesTag,
        visibility,
        changeVisibility,
        lensPreviewProps,
        discoverability,
        setDiscoverability,
        isGameUserProvided,
        setIsGameUserProvided,
        lensCreatorPayoutEnrolled,
        setLensCreatorPayoutEnrolled,
        lensDescription,
        setLensDescription,
        releaseNote,
        setReleaseNote,
    };
};
